import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, Select, message, Tooltip, Switch, Checkbox, Row, Col, InputNumber } from 'antd';
import { PlusOutlined, ReloadOutlined, PercentageOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { commissionPlansApi, productsApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatMoneyFa } from '../../utils/formatPriceToman';

const { Option } = Select;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const CommissionPlans = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await commissionPlansApi.getAll();
      if (res?.data?.commission_plans) {
        setPlans(Array.isArray(res.data.commission_plans) ? res.data.commission_plans : []);
      } else if (res?.commission_plans) {
        setPlans(Array.isArray(res.commission_plans) ? res.commission_plans : []);
      } else if (Array.isArray(res?.data)) {
        setPlans(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Error fetching commission plans:', error);
      message.error('خطا در دریافت طرح‌های پورسانت');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await productsApi.getAll();
      if (res?.data?.products) {
        setProducts(res.data.products);
      } else if (res?.products) {
        setProducts(res.products);
      } else if (Array.isArray(res?.data)) {
        setProducts(res.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchProducts();
  }, []);

  const handleCreate = async (values) => {
    setActionLoading(true);
    try {
      const payload = {
        ...values,
        tiers: values.tiers?.map(t => ({
          ...t,
          min_amount: Number(t.min_amount),
          max_amount: t.max_amount ? Number(t.max_amount) : null,
          percentage: Number(t.percentage),
          fixed_bonus: Number(t.fixed_bonus || 0),
        })) || [],
        product_rules: values.product_rules?.map(p => ({
          ...p,
          commission_rate: Number(p.commission_rate),
          min_profit_margin_percent: p.min_profit_margin_percent ? Number(p.min_profit_margin_percent) : null,
        })) || []
      };
      await commissionPlansApi.create(payload);
      message.success('طرح پورسانت جدید با موفقیت ایجاد شد');
      setIsModalVisible(false);
      form.resetFields();
      fetchPlans();
    } catch (error) {
      message.error('خطا در ایجاد طرح پورسانت');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نام طرح</span>,
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span className="font-medium" style={{ fontFamily: 'CustomFont' }}>{text}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نوع محاسبه</span>,
      dataIndex: 'calculation_type',
      key: 'calculation_type',
      render: (type) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {type === 'progressive_tiers' ? 'پلکانی' : type === 'fixed_percentage' ? 'درصد ثابت' : type}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>اعمال بر مبالغ تسویه‌نشده</span>,
      dataIndex: 'applies_to_unsettled',
      key: 'applies_to_unsettled',
      render: (applies) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {applies ? 'بله' : 'خیر'}
        </span>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                  <PercentageOutlined className="text-indigo-600 dark:text-indigo-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0" style={{ fontFamily: 'CustomFont' }}>
                    طرح‌های پورسانت
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    تعریف و مدیریت طرح‌های پورسانت پلکانی و تفکیکی
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip title="بروزرسانی">
                  <Button icon={<ReloadOutlined />} onClick={fetchPlans} size="middle" loading={loading} />
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsModalVisible(true)}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  طرح پورسانت جدید
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={3} />
            ) : (
              <Card className="dark:bg-gray-800 border-0 shadow-sm">
                <Table
                  columns={columns}
                  dataSource={plans}
                  rowKey="id"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>تعریف طرح پورسانت جدید</span>}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} initialValues={{ applies_to_unsettled: false, calculation_type: 'progressive_tiers', tiers: [{ min_amount: 0, percentage: 0, fixed_bonus: 0 }] }}>
          <Form.Item name="name" label={<span style={{ fontFamily: 'CustomFont' }}>نام طرح</span>} rules={[{ required: true }]}>
            <Input style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="calculation_type" label={<span style={{ fontFamily: 'CustomFont' }}>نوع محاسبه</span>} rules={[{ required: true }]}>
                <Select style={{ fontFamily: 'CustomFont' }}>
                  <Option value="progressive_tiers">پلکانی</Option>
                  <Option value="fixed_percentage">درصد ثابت</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="applies_to_unsettled" valuePropName="checked" label={<span style={{ fontFamily: 'CustomFont' }}>اعمال بر مبالغ تسویه‌نشده؟</span>}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <div className="mb-4 text-gray-700 dark:text-gray-300 font-bold" style={{ fontFamily: 'CustomFont' }}>پله‌های پورسانت (Tiers)</div>
          <Form.List name="tiers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'min_amount']}
                      rules={[{ required: true, message: 'مبلغ حداقل الزامی است' }]}
                    >
                      <InputNumber placeholder="حداقل مبلغ" style={{ fontFamily: 'CustomFont', width: '130px' }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'max_amount']}
                    >
                      <InputNumber placeholder="حداکثر مبلغ (اختیاری)" style={{ fontFamily: 'CustomFont', width: '150px' }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'percentage']}
                      rules={[{ required: true, message: 'درصد الزامی است' }]}
                    >
                      <InputNumber placeholder="درصد" style={{ fontFamily: 'CustomFont', width: '90px' }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'fixed_bonus']}
                    >
                      <InputNumber placeholder="پاداش ثابت" style={{ fontFamily: 'CustomFont', width: '120px' }} />
                    </Form.Item>
                    <Button danger onClick={() => remove(name)}>حذف</Button>
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ fontFamily: 'CustomFont' }}>
                    افزودن پله جدید
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <div className="mb-4 text-gray-700 dark:text-gray-300 font-bold mt-4" style={{ fontFamily: 'CustomFont' }}>قوانین پورسانت محصول (اختیاری)</div>
          <Form.List name="product_rules">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'product_id']}
                      rules={[{ required: true, message: 'محصول الزامی است' }]}
                    >
                      <Select showSearch optionFilterProp="children" placeholder="انتخاب محصول" style={{ fontFamily: 'CustomFont', width: '200px' }}>
                        {products.map(p => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'commission_rate']}
                      rules={[{ required: true, message: 'درصد الزامی است' }]}
                    >
                      <InputNumber placeholder="درصد پورسانت" style={{ fontFamily: 'CustomFont', width: '130px' }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'min_profit_margin_percent']}
                    >
                      <InputNumber placeholder="حداقل حاشیه سود (%)" style={{ fontFamily: 'CustomFont', width: '160px' }} />
                    </Form.Item>
                    <Button danger onClick={() => remove(name)}>حذف</Button>
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ fontFamily: 'CustomFont' }}>
                    افزودن قانون محصول
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => setIsModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}>
                ذخیره طرح
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default CommissionPlans;
